/*
  dev-reveal — a quiet fade-and-rise for the main content blocks of the dev-*
  sections as they scroll into view (section intros, cards, media + copy
  splits, CTAs, FAQ items, reviews, footer columns).

  How it stays safe:
  - Nothing is hidden by CSS alone. The rules in snippets/dev-reveal.liquid only
    match `html.dev-reveal [data-dev-reveal="pending"]`, and both the class and
    the attribute are set here. No JS, or this file failing → everything shows.
  - Only blocks that are entirely BELOW the viewport when this runs are hidden.
    Whatever is already on screen (hero, header, buy box, the first viewport)
    is never touched, so first paint and LCP are unchanged.
  - Opacity + the `translate` property only (it composes with any transform a
    block already has). Nothing changes size, margin or display, so no layout
    shift. will-change is set only while a block is playing.
  - Sticky blocks, blocks that already use `translate` for their own placement,
    and items whose parent scrolls sideways (carousels with scroll snapping)
    get opacity only. A sideways scroller's items are all
    revealed together when the scroller itself enters, since off-screen items
    in it would never intersect the viewport.
  - Each card or text group moves as ONE unit (image and text together); there
    is no stagger inside a block. Blocks of the same section entering together
    play 80ms apart in reading order, capped at 4 steps so lists don't crawl.
  - Motion starts just before a block enters the viewport (15% early), and
    waits (max 500ms) for the block's images to decode, so an image never
    appears after its card. Lazy images of upcoming blocks are switched to
    eager about 1.5 viewports ahead.
  - 900ms, cubic-bezier(0.16, 1, 0.3, 1), opacity 0 → 1 and a 24px rise
    (14px for blocks taller than 60% of the viewport), played as the block is
    10% into view; siblings 110ms apart, up to 6 steps. No scale, no blur.
  - Excluded: header / mega menu / mobile header, announcement bars, heroes,
    the product buy box, dialogs and modals (sizing guide, nutrition label),
    the cart drawer, the stat-grid cards (assets/dev-stat-anim.js) and count-up
    numbers (assets/dev-countup.js), the word-by-word scroll statement, and
    form fields on their own.
  - prefers-reduced-motion, the theme editor (Shopify.designMode) and the
    theme setting "Fade content in on scroll" turned off: nothing is hidden.
*/
(function () {
  'use strict';

  if (window.devReveal) return;

  var html = document.documentElement;
  var DURATION = 900;
  var STEP = 110;
  var MAX_STEPS = 5;
  var IMAGE_WAIT = 500;

  var TARGETS = [
    // Section intros
    '.dev-card-grid__head',
    '.dev-product-range__head',
    '.dev-reviews-wall__head',
    '.dev-offer-stack__head',
    '.dev-feature-showcase__head',
    '.dev-spec-showcase__head',
    '.dev-ugc-row__head',
    '.dev-stat-grid__head',
    '.dev-media-statement__band',
    '.dev-faq__heading',
    '.dev-certifications__intro',
    // Media + copy splits (a stacked media-statement's image runs full width: copy only)
    '.dev-media-statement--split .dev-media-statement__media',
    '.dev-media-statement__copy',
    '.dev-split-feature__media',
    '.dev-split-feature__copy',
    '.dev-facility__media',
    '.dev-facility__copy',
    '.dev-feature-showcase__media',
    // Cards, rows and lists (each item is one block; staggered)
    '.dev-card-grid__grid > *',
    '.dev-product-range__grid > *',
    '.dev-reviews-wall__wall > *',
    '.dev-faq__list > *',
    '.dev-feature-showcase__rows > *',
    '.dev-certifications__list > *',
    '.dev-trust-bar__bar > *',
    '.dev-ugc-row__row > *',
    '.dev-blog-featured__grid > *',
    '.dev-blog-list__grid > *',
    '.dev-related__grid > *',
    '.dev-offer-stack__stack > :not(.dev-offer-group)',
    '.dev-offer-group__items > *',
    '.dev-spec-showcase__stage > *',
    // Single blocks
    '.dev-comparison-table__card',
    '.dev-guarantee-banner__panel',
    '.dev-blog-list__toolbar',
    // CTAs
    '.dev-card-grid__actions',
    '.dev-feature-showcase__actions',
    '.dev-spec-showcase__actions',
    '.dev-reviews-wall__cta',
    '.dev-stat-grid__actions',
    '.dev-faq__actions',
    // Footer
    '.dev-footer__top',
    '.dev-footer__columns > *',
    '.dev-footer__bottom'
  ].join(',');

  var SKIP = [
    '#header-group',
    'header-component',
    '.dev-mheader',
    '.dev-announcement',
    '.dev-hero',
    '.dev-page-hero',
    '.dev-app-hero',
    '.dev-media-tiles',
    '.dev-buybox',
    '.dev-scroll-statement',
    '.dev-stat-grid__cards',
    '[data-dev-countup]',
    'dialog',
    '.dev-modal',
    'cart-drawer',
    '#cart-drawer-section',
    '.dev-sizing-guide-vars',
    '.dev-nutrition-vars',
    '[data-dev-reveal-skip]',
    'script, style, template, input, select, textarea, button, label'
  ].join(',');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var pending = [];
  var io = null;
  var near = null;
  var sweepQueued = false;

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode) || html.classList.contains('shopify-design-mode');
  }

  function isSideScroller(el) {
    if (!el || el === document.body) return false;
    var o = getComputedStyle(el).overflowX;
    return (o === 'auto' || o === 'scroll') && el.scrollWidth > el.clientWidth + 1;
  }

  // Horizon scrolls the page inside .page-wrapper on desktop (html/body are
  // overflow: hidden). Its overflow clips anything outside it, so an early
  // trigger margin only works when the observer's root is that wrapper.
  function scrollRoot() {
    var w = document.querySelector('.page-wrapper');
    if (w && /auto|scroll/.test(getComputedStyle(w).overflowY) && w.scrollHeight > w.clientHeight) return w;
    return null;
  }

  function sectionOf(el) {
    return el.closest('.shopify-section') || el.closest('footer') || document.body;
  }

  // Shows a block right away, no animation.
  function show(el) {
    el.removeAttribute('data-dev-reveal');
    el.removeAttribute('data-dev-reveal-fade');
    el.style.removeProperty('--dev-reveal-delay');
    el.style.removeProperty('--dev-reveal-y');
    if (io) io.unobserve(el);
    if (near) near.unobserve(el);
    var i = pending.indexOf(el);
    if (i !== -1) pending.splice(i, 1);
  }

  // Lazy images inside a block about to play start loading now, so a card
  // never fades in with an empty image box.
  function wakeImages(el) {
    var imgs = el.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].loading === 'lazy') imgs[i].loading = 'eager';
    }
    return imgs;
  }

  function imageReady(img) {
    if (img.complete) {
      return img.naturalWidth && img.decode ? img.decode().catch(function () {}) : Promise.resolve();
    }
    return new Promise(function (resolve) {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  // Resolves once the batch's images are decoded, or after IMAGE_WAIT.
  function imagesReady(els) {
    var waits = [];
    els.forEach(function (el) {
      var imgs = wakeImages(el);
      for (var i = 0; i < imgs.length; i++) {
        // Only images that are actually rendered at this width.
        if (imgs[i].getClientRects().length) waits.push(imageReady(imgs[i]));
      }
    });
    if (!waits.length) return Promise.resolve();
    return Promise.race([
      Promise.all(waits),
      new Promise(function (resolve) {
        setTimeout(resolve, IMAGE_WAIT);
      })
    ]);
  }

  function play(el, step) {
    var i = pending.indexOf(el);
    if (i === -1) return;
    pending.splice(i, 1);
    if (io) io.unobserve(el);
    if (near) near.unobserve(el);
    var delay = Math.min(step, MAX_STEPS) * STEP;
    if (delay) el.style.setProperty('--dev-reveal-delay', delay + 'ms');
    el.setAttribute('data-dev-reveal', 'play');
    var done = function () {
      if (el.getAttribute('data-dev-reveal') !== 'play') return;
      el.removeAttribute('data-dev-reveal');
      el.removeAttribute('data-dev-reveal-fade');
      el.removeAttribute('data-dev-reveal-tf');
      el.style.removeProperty('--dev-reveal-delay');
      el.style.removeProperty('--dev-reveal-y');
    };
    setTimeout(done, DURATION + delay + 80);
  }

  // Plays a batch of blocks, staggered per section in reading order.
  function playBatch(els) {
    if (!els.length) return;
    imagesReady(els).then(function () {
      requestAnimationFrame(function () {
        startBatch(els);
      });
    });
  }

  function startBatch(els) {
    els = els.filter(function (el) {
      return pending.indexOf(el) !== -1;
    });
    if (!els.length) return;
    var items = els.map(function (el) {
      var r = el.getBoundingClientRect();
      return { el: el, top: Math.round(r.top / 24), left: r.left, section: sectionOf(el) };
    });
    items.sort(function (a, b) {
      if (a.section !== b.section) {
        return a.section.compareDocumentPosition(b.section) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      }
      return a.top - b.top || a.left - b.left;
    });
    var counters = new Map();
    items.forEach(function (it) {
      var n = counters.get(it.section) || 0;
      counters.set(it.section, n + 1);
      play(it.el, n);
    });
  }

  function onIntersect(entries) {
    var batch = [];
    entries.forEach(function (entry) {
      var el = entry.target;
      if (!entry.isIntersecting) {
        // Scrolled past without ever being seen (a jump): just show it.
        if (entry.boundingClientRect.bottom <= 0) show(el);
        return;
      }
      io.unobserve(el);
      if (el.__devRevealGroup) {
        el.__devRevealGroup.forEach(function (item) {
          if (pending.indexOf(item) !== -1) batch.push(item);
        });
      } else {
        batch.push(el);
      }
    });
    playBatch(batch);
  }

  // Safety net for fast jumps (End key, anchor links): anything already
  // above the viewport is shown without animation.
  function sweep() {
    sweepQueued = false;
    pending.slice().forEach(function (el) {
      if (el.getBoundingClientRect().bottom <= 0) show(el);
    });
  }

  function onScroll() {
    if (sweepQueued || !pending.length) return;
    sweepQueued = true;
    setTimeout(function () {
      requestAnimationFrame(sweep);
    }, 150);
  }

  function enabled() {
    return !reduce.matches && !designMode() && 'IntersectionObserver' in window;
  }

  function scan(root) {
    if (!enabled()) return;
    root = root || document;
    html.classList.add('dev-reveal');

    if (!io) {
      var rootEl = scrollRoot();
      // Plays as a block comes into view (10% up from the bottom edge), so the
      // motion is seen. Image readiness is handled separately (near + wait),
      // which is what keeps it from reading as content still loading.
      io = new IntersectionObserver(onIntersect, {
        root: rootEl,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0
      });
      // Further out: get the images of upcoming blocks loading.
      near = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            near.unobserve(entry.target);
            var items = entry.target.__devRevealGroup || [entry.target];
            items.forEach(wakeImages);
          });
        },
        { root: rootEl, rootMargin: '0px 0px 150% 0px', threshold: 0 }
      );
    }

    var vh = window.innerHeight;
    var found = root.querySelectorAll(TARGETS);
    var chosen = [];

    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el.hasAttribute('data-dev-reveal')) continue;
      if (el.closest(SKIP)) continue;
      if (io.root && !io.root.contains(el)) continue;
      // Animate whole blocks, never a block inside another animated block.
      var nested = false;
      for (var j = 0; j < chosen.length; j++) {
        if (chosen[j].contains(el)) {
          nested = true;
          break;
        }
      }
      if (nested) continue;
      var r = el.getBoundingClientRect();
      if (!r.width && !r.height) continue; // display: none at this width
      // Already on screen, or above it: leave it exactly as it is.
      if (r.top < vh) continue;
      chosen.push(el);
    }

    var groups = new Map();
    chosen.forEach(function (el) {
      var parent = el.parentElement;
      var cs = getComputedStyle(el);
      // Sticky blocks, and blocks placed with their own `translate` (the spec
      // showcase cards), fade only: a rise would fight their positioning.
      var fade = cs.position === 'sticky' || cs.position === 'fixed' || (cs.translate && cs.translate !== 'none');
      // Review II (supplements: "Weird white line at the bottom"). A split
      // image that fills its section edge to edge has nothing behind it but
      // the section's light ground, so rising into place uncovered a strip of
      // it along the edge. Such media fades only, and does not move.
      var edgeMedia = false;
      if (!fade && el.matches('.dev-media-statement--split .dev-media-statement__media')) {
        var host = el.closest('.dev-media-statement');
        edgeMedia = !!host && Math.abs(host.getBoundingClientRect().height - el.getBoundingClientRect().height) < 2;
        if (edgeMedia) fade = true;
      }
      if (isSideScroller(parent)) {
        fade = true;
        if (!groups.has(parent)) groups.set(parent, []);
        groups.get(parent).push(el);
      }
      // Blocks placed with their own `translate` (the spec showcase cards)
      // rise through `transform` instead, which composes with it.
      if (fade && !edgeMedia && cs.position !== 'sticky' && cs.position !== 'fixed' && !isSideScroller(parent)) {
        fade = false;
        el.setAttribute('data-dev-reveal-tf', '');
      }
      if (fade) el.setAttribute('data-dev-reveal-fade', '');
      // Big blocks rise less, so they don't feel like they're dropping in.
      else if (el.getBoundingClientRect().height > vh * 0.6) el.style.setProperty('--dev-reveal-y', '14px');
      el.setAttribute('data-dev-reveal', 'pending');
      pending.push(el);
    });

    chosen.forEach(function (el) {
      if (!groups.has(el.parentElement)) {
        io.observe(el);
        near.observe(el);
      }
    });
    groups.forEach(function (items, scroller) {
      scroller.__devRevealGroup = items;
      io.observe(scroller);
      near.observe(scroller);
    });
  }

  function showAll() {
    pending.slice().forEach(show);
  }

  window.devReveal = { scan: scan, showAll: showAll };

  if (designMode()) {
    // Theme editor: everything stays visible, including re-rendered sections.
    document.addEventListener('shopify:section:load', function () {
      showAll();
    });
    return;
  }

  // The page scrolls inside .page-wrapper, not the window: listen in capture.
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('beforeprint', showAll);
  // Keyboard users never land on something invisible.
  document.addEventListener('focusin', function (event) {
    var el = event.target.closest && event.target.closest('[data-dev-reveal="pending"]');
    if (el) show(el);
  });
  if (reduce.addEventListener) {
    reduce.addEventListener('change', function () {
      if (reduce.matches) showAll();
    });
  }
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
})();
